import { InfoCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { useMemoizedFn, useMount } from 'ahooks';
import { Affix, Space, Spin, Tabs, Tooltip } from 'antd';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import React, { useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder } from 'services/api/refund';
import toLowerCase from 'utils/toLowerCase';
import CancelListItem from '../components/CancelListItem';
import Filter, { FilterChangeEvent } from '../components/OrderFilter/Filter';
import RefundDrawer from '../components/RefundDrawer';
import useCancelList from '../hooks/useCancelList';
import useFilter from '../hooks/useFilter';
import { usePlatform } from '../hooks/usePlatformList';
import { getCount } from '../utils/count';
import { useTransAppProductName } from 'hooks/useTrans';
import { FilterIdType } from '../utils/constants';
import { PaginationUI } from './ReturnList';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { OrderDrawer } from 'components/LightConversation/components/OrderDrawer';

const Container = styled.div`
  .ant-tabs {
    background-color: #ffffff;
  }
  .ant-tabs-nav {
    flex: 0 0 auto;
  }
  .ant-pagination {
    text-align: right;
  }
  > .ant-spin-nested-loading {
    flex: 1 0 0;
    padding: 0 16px 16px;

    > .ant-spin-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      > div {
        flex: 1 0 0;
      }
      > ul {
        flex: 0 0 auto;
      }
    }
  }
`;

const Table = styled.table`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  overflow: hidden;

  tr {
    display: flex;
    overflow: hidden;
  }
  td {
    padding: 0 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    box-sizing: border-box;
  }
  .td-title {
    width: 26%;
  }
  .td-amount {
    width: 12%;
    text-align: center;
  }
  .td-reason {
    width: 22%;
  }
  .td-status {
    width: 22%;
  }
  .td-action {
    width: 18%;
  }
`;

const CancelList = React.memo(() => {
  const platform = usePlatform()!;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const { query, onQueryChange } = useFilter();
  const { update, visible, hide, meta } = useModal(false);
  const {
    list = [],
    statusFilter,
    conversationList = [],
    setStatusFilter,
    loading,
    statusCount,
    pagination: { pageSize, current, onChange, total },
    refresh,
  } = useCancelList(query, platform);
  const modalMetaRef = useRef<{
    row: CancelOrder;
    conversation?: Conversation.Conversation;
  } | null>(null);
  const isLightLayout = useIsLightChatLayout();

  const handleFilterChange = useMemoizedFn((e: FilterChangeEvent) => {
    const { snType, sn } = e;
    const { orderSn, returnSn, returnTrackingNumber } = query;
    if (
      (snType === FilterIdType.orderId && sn !== orderSn) ||
      (snType === FilterIdType.returnId && sn !== returnSn) ||
      (snType === FilterIdType.returnTrackingNumber && sn !== returnTrackingNumber)
    ) {
      setStatusFilter('ALL');
    }

    onQueryChange(e);
  });

  useEffect(() => {
    if (visible && modalMetaRef.current?.row) {
      const {
        current: { row },
      } = modalMetaRef;
      const nextRow = list.find((it) => it.orderId === row.orderId);
      modalMetaRef.current.row = nextRow || row;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);
  useMount(() => {
    posthog.capture('$cancelOrder', {
      type: 'entryPage',
    });
  });

  let detailDrawer = null;
  if (modalMetaRef.current?.row) {
    const { row, conversation } = modalMetaRef.current;
    if (isLightLayout && conversation) {
      detailDrawer = (
        <OrderDrawer
          visible={visible}
          conversation={conversation}
          sendable={false}
          onClose={() => hide()}
          entryType="cancel"
          cancelOrder={row}
          defaultActiveTab="cancelDetail"
          contactEnable
        />
      );
    } else {
      detailDrawer = (
        <RefundDrawer
          platform={toLowerCase(platform)}
          visible={visible}
          onClose={() => hide()}
          onUpdate={refresh}
          key={`${meta}`}
          type="cancel"
          row={row}
          conversation={conversation}
        />
      );
    }
  }

  return (
    <>
      <Filter filterType="cancel" onChange={handleFilterChange} />
      <Container className="flex flex-grow flex-shrink-0 basis-0 flex-col bg-[#fff] mx-4 my-4">
        <Affix target={() => document.querySelector<HTMLDivElement>('.refund-container')}>
          <Tabs activeKey={statusFilter} onChange={(key: any) => setStatusFilter(key)}>
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.all')}(${getCount(
                statusCount?.total,
              )})`}
              key="ALL"
            />
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.pending')}(${getCount(
                statusCount?.pendingCount,
              )})`}
              key="PENDING"
            />
            <Tabs.TabPane
              key="PROCESS"
              tab={
                <Space>
                  {`${t('scene.orderWorkspace.cancelOrderTab.tab.processing')}(${getCount(
                    statusCount?.processedCount,
                  )})`}
                  <Tooltip title={appProductNameT('ViewRefund.cancelPendingTabTips')}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
            />
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.completed')}(${getCount(
                statusCount?.completeCount,
              )})`}
              key="COMPLETE"
            />
          </Tabs>
        </Affix>

        <Spin spinning={loading}>
          <Table>
            <thead className="bg-[#f7f9fa] h-[56px] leading-[56px] text-[#232426] font-medium">
              <tr>
                <td className="td-title">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.product')}
                </td>
                <td className="td-amount">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.amount')}
                </td>
                <td className="td-reason">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.cancelReason')}
                </td>
                <td className="td-status">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.status')}
                </td>
                <td className="td-action">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.action')}
                </td>
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <CancelListItem
                  row={row}
                  conversation={conversationList[idx]}
                  platform={platform}
                  key={row.orderId + platform + String(idx) + row.orderState}
                  refreshList={refresh}
                  onClick={() => {
                    modalMetaRef.current = { row, conversation: conversationList[idx] };
                    update(true, `${row.orderId}-${idx}`);
                  }}
                />
              ))}
              {!list?.length && !loading && <Empty />}
            </tbody>
          </Table>
          <PaginationUI
            className="!mt-4"
            pageSize={pageSize}
            current={current}
            onChange={onChange}
            total={total}
            showSizeChanger
            pageSizeOptions={['10', '20', '100']}
          />
        </Spin>
      </Container>
      {detailDrawer}
    </>
  );
});

export default CancelList;
